import { ListAllWithPathOutput, uploadData, list } from '@aws-amplify/storage';
import normalize from 'path-normalize';
import { Observable, defer, from, map, switchMap, take } from 'rxjs';
import { companyConfigService } from '../../../services/companyConfig/companyConfig.service';
import { authService } from '../../../services/auth/auth.service';
import {
  createSessionExpiredError,
  refreshIdTokenOnce,
} from '../../../services/auth/sessionExpiry.helper';

/**
 * Worded so `responseHandler` classifies it as a transient connection drop
 * (`CONNECTION_LOST_PATTERNS` includes "a network error has occurred"): the user gets
 * `errors.connectionLost` and no Sentry ticket is filed, the same treatment WA-3800 gives
 * the underlying identity-pool failure.
 */
export const IDENTITY_POOL_UNREACHABLE_MESSAGE =
  'A network error has occurred: the Cognito identity pool could not be reached, so the ' +
  'private S3 prefix of this user cannot be resolved.';

/**
 * Resolve the identity the private S3 prefix (`private/{identityId}/…`) is keyed on, or
 * fail the way every other authenticated request in this app fails (WA-3864).
 *
 * `uploadData`/`list` used to receive the prefix as a callback and let Amplify resolve
 * the identity itself. Amplify then reports a missing identity as the raw validation
 * error `Missing identity ID when accessing objects in protected or private access
 * level.` — which is what the user saw as `errors.unknown` (and Sentry filed as
 * WAWIASSIST-24P) when their refresh token died between two GraphQL calls and the
 * upload was the first request to notice. Resolving the identity here instead lets the
 * two possible causes get their existing handling:
 *
 *   - no idToken even after one forced refresh → the session is over. Same contract as
 *     `gqlClient`: the error is marked as expected (no Sentry) and the user is
 *     redirected to login.
 *   - an idToken but no identityId → the identity pool was unreachable and the
 *     network-tolerant credentials provider (WA-3800) swallowed the failure. That is a
 *     connection drop, so it is worded as one.
 */
const resolveStorageIdentityId = (): Observable<string> =>
  authService.sub.idToken().pipe(
    take(1),
    switchMap((idToken) => (idToken ? Promise.resolve(idToken) : refreshIdTokenOnce())),
    switchMap((idToken) => {
      if (!idToken) throw createSessionExpiredError();
      return authService.sub.userIdentityId().pipe(take(1));
    }),
    map((identityId) => {
      if (!identityId) throw new Error(IDENTITY_POOL_UNREACHABLE_MESSAGE);
      return identityId;
    }),
  );

/** `private/{identityId}/{companyId}/{key}` — the per-user, per-company S3 prefix. */
const resolvePrivatePath = (key: string): Observable<string> =>
  resolveStorageIdentityId().pipe(
    switchMap((identityId) =>
      companyConfigService.getConfigs().pipe(
        take(1),
        map(({ id }) => normalize(`private/${identityId}/${id}/${key}`)),
      ),
    ),
  );

class File {
  /**
   * By default upload file to private folder in s3 bucket
   */
  upload(
    key: string,
    file: Parameters<typeof uploadData>[0]['data'],
  ): Observable<{ path: string }> {
    return resolvePrivatePath(key).pipe(
      switchMap((path) =>
        defer(() => from(uploadData({ path, data: file }).result)).pipe(
          map((result) => ({ path: result.path })),
        ),
      ),
    );
  }
  /**
   * By default get list of files from private folder in s3 bucket
   */
  lists(key: string): Observable<ListAllWithPathOutput> {
    return resolvePrivatePath(key).pipe(
      switchMap((path) => defer(() => from(list({ path, options: { listAll: true } })))),
    );
  }
}

export const filesHelper = new File();
